import type { Operation } from './outbox';

/**
 * The outbox holds requests; people saved pieces of work. A Stock IN with
 * "Receive and post" is two requests — the receipt and its post — and a
 * supplier typed on it is a third, but it was one Save, and is one line on
 * Offline work and one in the count of what waits to be sent.
 */
export interface Piece {
  /** The batch's own key: stable while the piece is on the list. */
  id: string;
  /** The request that names the piece: the document, not its post or supplier. */
  lead: Operation;
  /** Every request saved with it, in the order they go. */
  ops: Operation[];
  /** Refused if any step was; waiting while any has not gone; else sent. */
  state: 'waiting' | 'refused' | 'sent';
  /** The step the server refused, when one was. */
  refused: Operation | null;
  createdAt: number;
  /** When its last step went, once all have. */
  sentAt: number | null;
  /** Saved with no answer from the server. */
  keptOffline: boolean;
  /** The number the server gave the document, once it has. */
  documentNo: string | null;
}

function batchOf(op: Operation): string {
  // Work saved before batches were recorded: one save, one moment.
  return op.batch ?? `at:${op.createdAt}`;
}

/** The step a piece is named after: what carries the form, else what is not a supplier or item added with it. */
function leadOf(ops: readonly Operation[]): Operation {
  return ops.find((op) => op.form !== undefined && op.lane !== 'reference') ?? ops.find((op) => op.lane !== 'reference') ?? ops[0]!;
}

/** Groups the outbox into what was saved, oldest first. */
export function piecesOf(all: readonly Operation[]): Piece[] {
  const groups = new Map<string, Operation[]>();
  for (const op of all) {
    const key = batchOf(op);
    const group = groups.get(key);
    if (group) group.push(op);
    else groups.set(key, [op]);
  }
  return [...groups.entries()].map(([id, ops]) => {
    const lead = leadOf(ops);
    const refused = ops.find((op) => op.state === 'refused') ?? null;
    const state = refused ? 'refused' : ops.every((op) => op.state === 'sent') ? 'sent' : 'waiting';
    // A document is known by its number; an item, unit or supplier by its code.
    const made = lead.result as { documentNo?: unknown; code?: unknown } | null;
    return {
      id,
      lead,
      ops,
      state,
      refused,
      createdAt: Math.min(...ops.map((op) => op.createdAt)),
      sentAt: state === 'sent' ? Math.max(...ops.map((op) => op.sentAt ?? 0)) : null,
      keptOffline: ops.some((op) => op.keptOffline),
      documentNo:
        made && typeof made.documentNo === 'string' ? made.documentNo : made && typeof made.code === 'string' ? made.code : null,
    };
  });
}
